import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { activity, flyers as flyersApi, social as socialApi } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import SocialPlatformIcon from '../components/SocialPlatformIcon';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  Copy,
  Download, 
  Mail, 
  Printer,
  Share2,
  Sparkles,
  Smartphone,
  FileImage,
  Check,
  Loader,
  Maximize2,
  X,
  ZoomIn,
  ZoomOut,
  ExternalLink,
  Send
} from 'lucide-react';

const DESKTOP_SOCIAL_URLS = {
  linkedin: 'https://www.linkedin.com/feed/?shareActive=true',
  facebook: 'https://www.facebook.com/',
  instagram: 'https://www.instagram.com/'
};

const SOCIAL_LABELS = { linkedin: 'LinkedIn', facebook: 'Facebook', instagram: 'Instagram' };

const cleanWebsite = (website) => String(website || '')
  .trim()
  .replace(/^https?:\/\//i, '')
  .replace(/\/$/, '');

const buildContactCallToAction = (user) => {
  const company = String(user?.companyName || '').trim();
  const contact = user?.flyerContactVisibility?.contactName === false ? '' : String(user?.contactName || '').trim();
  const website = user?.flyerContactVisibility?.website === false ? '' : cleanWebsite(user?.website);
  const websiteSuffix = website ? ` — ${website}` : '';

  if (contact && company && contact.toLowerCase() !== company.toLowerCase()) {
    return `Questions? Contact ${contact} at ${company}${websiteSuffix}.`;
  }
  if (company) return `Questions? ${company} is here to help${websiteSuffix}.`;
  if (contact) return `Questions? Contact ${contact}${websiteSuffix}.`;
  if (website) return `Learn more at ${website}.`;
  return '';
};

const formatHashtags = (hashtags = []) => hashtags
  .map(tag => String(tag).trim().replace(/^#+/, ''))
  .filter(Boolean)
  .map(tag => `#${tag}`)
  .join(' ');

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) throw new Error('Clipboard access is unavailable');
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const FlyerDetail = () => {
  const { id } = useParams();
  const { user, supportSession } = useAuth();
  const [flyer, setFlyer] = useState(null);
  const [sizes, setSizes] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewActualSize, setIsPreviewActualSize] = useState(false);
  const [socialCaption, setSocialCaption] = useState('');
  const [socialHashtags, setSocialHashtags] = useState('');
  const [shareAsset, setShareAsset] = useState(null);
  const [supportsNativeFileShare, setSupportsNativeFileShare] = useState(false);
  const [socialPlatforms, setSocialPlatforms] = useState([]);
  const [publishingPlatform, setPublishingPlatform] = useState('');
  const previewPageCount = selectedSize?.category === 'print'
    ? Math.max(1, Number.parseInt(flyer?.pageCount, 10) || 1)
    : 1;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [flyerRes, sizesRes] = await Promise.all([
          flyersApi.getOne(id),
          flyersApi.getSizes()
        ]);
        setFlyer(flyerRes.data);
        setSizes(sizesRes.data.sizes);
        if (sizesRes.data.sizes.length > 0) {
          setSelectedSize(sizesRes.data.sizes[0]);
        }
      } catch (error) {
        console.error('Error fetching flyer:', error);
        toast.error('Failed to load flyer');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    try {
      const probe = new File(['share'], 'share.txt', { type: 'text/plain' });
      setSupportsNativeFileShare(
        typeof navigator.share === 'function'
        && (typeof navigator.canShare !== 'function' || navigator.canShare({ files: [probe] }))
      );
    } catch {
      setSupportsNativeFileShare(false);
    }

    socialApi.getConnections()
      .then(response => setSocialPlatforms(response.data.platforms || []))
      .catch(error => console.warn('Social connection status unavailable:', error.message));
  }, []);

  useEffect(() => {
    if (!flyer) return;
    const contactCallToAction = buildContactCallToAction(user);
    setSocialCaption([flyer.socialCaption, contactCallToAction].filter(Boolean).join('\n\n'));
    setSocialHashtags(formatHashtags(flyer.socialHashtags));
  }, [flyer, user]);

  useEffect(() => {
    setShareAsset(null);
  }, [id, selectedSize?.slug]);

  useEffect(() => {
    if (!selectedSize) return undefined;

    let objectUrls = [];
    let cancelled = false;

    const loadPreview = async () => {
      try {
        const responses = await Promise.all(
          Array.from({ length: previewPageCount }, (_value, index) => (
            flyersApi.preview(id, selectedSize.slug, index + 1)
          ))
        );
        if (cancelled) return;
        objectUrls = responses.map(response => (
          window.URL.createObjectURL(new Blob([response.data], { type: 'image/png' }))
        ));
        setPreviewUrls(objectUrls);
      } catch (error) {
        if (!cancelled) {
          console.error('Preview error:', error);
          setPreviewUrls([]);
        }
      }
    };

    setPreviewUrls([]);
    setIsPreviewOpen(false);
    setIsPreviewActualSize(false);
    loadPreview();

    return () => {
      cancelled = true;
      objectUrls.forEach(objectUrl => window.URL.revokeObjectURL(objectUrl));
    };
  }, [id, selectedSize, previewPageCount]);

  useEffect(() => {
    if (!isPreviewOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsPreviewOpen(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isPreviewOpen]);

  const handleDownload = async () => {
    if (!selectedSize) {
      toast.error('Please select a size first');
      return;
    }
    setDownloading(true);
    try {
      const response = await flyersApi.download(id, selectedSize.slug);
      const isPdf = selectedSize.category === 'print';
      const blob = new Blob([response.data], {
        type: isPdf ? 'application/pdf' : 'image/png'
      });
      downloadBlob(
        blob,
        `${flyer.title.replace(/[^a-z0-9]/gi, '_')}_${selectedSize.slug}.${isPdf ? 'pdf' : 'png'}`
      );
      toast.success('Flyer downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download flyer');
    } finally {
      setDownloading(false);
    }
  };

  const getSocialPostText = () => [socialCaption.trim(), socialHashtags.trim()]
    .filter(Boolean)
    .join('\n\n');

  const handleCopySocialPost = async () => {
    const socialPost = getSocialPostText();
    if (!socialPost) {
      toast.error('There is no social post to copy');
      return;
    }

    try {
      await copyToClipboard(socialPost);
      activity.track({
        eventType: 'caption_copy',
        flyerId: id,
        outputSizeId: selectedSize?.id || null
      }).catch(() => {});
      toast.success('Caption and hashtags copied!');
    } catch (error) {
      console.error('Copy social post error:', error);
      toast.error('Could not copy automatically. Select the text and copy it manually.');
    }
  };

  const downloadShareFallback = async (file) => {
    downloadBlob(file, file.name);
    try {
      await copyToClipboard(getSocialPostText());
      toast.success('Image downloaded and caption copied!');
    } catch (error) {
      console.error('Share fallback copy error:', error);
      toast.success('Image downloaded. Use the copy button for the caption.');
    }
  };

  const openShareSheet = async (file, preparedThisClick) => {
    let canShareFile = false;
    if (typeof navigator.share === 'function') {
      try {
        canShareFile = typeof navigator.canShare !== 'function' || navigator.canShare({ files: [file] });
      } catch (error) {
        console.warn('File sharing capability check failed:', error);
      }
    }

    if (!canShareFile) {
      await downloadShareFallback(file);
      return;
    }

    try {
      await navigator.share({
        title: flyer.title,
        text: getSocialPostText(),
        files: [file]
      });
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (error?.name === 'NotAllowedError' && preparedThisClick) {
        toast.success('Your image is ready. Tap Share again to open your apps.');
        return;
      }
      console.error('Native share error:', error);
      await downloadShareFallback(file);
    }
  };

  const getShareSize = () => (
    selectedSize?.category === 'social'
      ? selectedSize
      : sizes.find(size => size.slug === 'social-post') || sizes.find(size => size.category === 'social')
  );

  const prepareShareAsset = async () => {
    const shareSize = getShareSize();
    if (!shareSize) throw new Error('No social image size is available');
    const shareKey = `${id}:${shareSize.slug}`;
    if (shareAsset?.key === shareKey) return { file: shareAsset.file, shareSize };

    const response = await flyersApi.download(id, shareSize.slug, 'share');
    const filename = `${flyer.title.replace(/[^a-z0-9]/gi, '_')}_${shareSize.slug}.png`;
    const file = new File([response.data], filename, { type: 'image/png' });
    setShareAsset({ key: shareKey, file });
    return { file, shareSize };
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const prepared = await prepareShareAsset();
      await openShareSheet(prepared.file, !shareAsset);
    } catch (error) {
      console.error('Prepare share image error:', error);
      toast.error(error.message || 'Failed to prepare the branded social image');
    } finally {
      setSharing(false);
    }
  };

  const handleDesktopPlatform = async (platform) => {
    window.open(DESKTOP_SOCIAL_URLS[platform], '_blank', 'noopener,noreferrer');
    setSharing(true);
    try {
      const { file } = await prepareShareAsset();
      await downloadShareFallback(file);
      activity.track({
        eventType: 'social_platform_open',
        flyerId: id,
        outputSizeId: getShareSize()?.id || null,
        metadata: { platform }
      }).catch(() => {});
    } catch (error) {
      console.error('Desktop social preparation error:', error);
      toast.error(error.message || 'Failed to prepare the post');
    } finally {
      setSharing(false);
    }
  };

  const handleDirectPublish = async (platform) => {
    const item = socialPlatforms.find(entry => entry.platform === platform);
    const label = SOCIAL_LABELS[platform];
    if (!item?.connected) return;
    if (!window.confirm(`Publish this branded flyer publicly to ${item.accountName || label}?`)) return;

    setPublishingPlatform(platform);
    try {
      const shareSize = sizes.find(size => size.slug === 'social-post') || getShareSize();
      const response = await socialApi.publish(platform, {
        flyerId: id,
        sizeSlug: shareSize?.slug || 'social-post',
        caption: getSocialPostText()
      });
      toast.success(`Published to ${response.data.accountName || label}!`);
    } catch (error) {
      toast.error(error.response?.data?.error || `Could not publish to ${label}`);
    } finally {
      setPublishingPlatform('');
    }
  };

  const handleEmail = async () => {
    if (!selectedSize) {
      toast.error('Please select a size first');
      return;
    }
    setEmailing(true);
    try {
      const response = await flyersApi.email(id, selectedSize.slug);
      toast.success(response.data?.message || 'Flyer accepted for delivery. Check your inbox or junk folder.');
    } catch (error) {
      console.error('Email error:', error);
      toast.error('Failed to send email');
    } finally {
      setEmailing(false);
    }
  };

  const getSizeIcon = (category) => {
    return category === 'print' ? <Printer size={20} /> : <Smartphone size={20} />;
  };

  const isPrintPdf = selectedSize?.category === 'print';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!flyer) {
    return (
      <div className="text-center py-16">
        <FileImage className="mx-auto text-gray-300 mb-4" size={64} />
        <h3 className="text-xl font-semibold text-gray-600 mb-2">Flyer not found</h3>
        <Link to="/flyers" className="text-primary-500 hover:text-primary-600">Back to library</Link>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <Link to="/flyers" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6">
        <ArrowLeft size={20} /> Back to Library
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Preview */}
        <div className="order-2 lg:order-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="font-semibold text-gray-800">Preview</h2>
              {previewPageCount > 1 && (
                <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold text-primary-700">
                  {previewPageCount} pages
                </span>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center min-h-[400px]">
              {previewUrls.length === previewPageCount ? (
                <div className={`grid w-full items-start gap-3 ${previewPageCount > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {previewUrls.map((previewUrl, index) => (
                    <button
                      key={`${selectedSize?.slug}-${index + 1}`}
                      type="button"
                      onClick={() => setIsPreviewOpen(true)}
                      className="group relative mx-auto block w-full max-w-fit cursor-zoom-in rounded-lg text-left focus:outline-none focus:ring-4 focus:ring-primary-200"
                      aria-label={`Expand page ${index + 1} of ${previewPageCount}`}
                    >
                      <img
                        src={previewUrl}
                        alt={`${flyer.title} - page ${index + 1} of ${previewPageCount}`}
                        className="max-h-[500px] w-full rounded-lg shadow-md transition group-hover:shadow-xl"
                      />
                      <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-gray-950/80 px-2.5 py-1 text-[11px] font-medium text-white opacity-90 shadow-sm transition group-hover:bg-primary-600">
                        <Maximize2 size={13} /> Expand
                      </span>
                      {previewPageCount > 1 && (
                        <span className="absolute bottom-2 left-2 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-gray-700 shadow-sm">
                          Page {index + 1}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center">
                  <Loader className="mx-auto mb-3 animate-spin text-primary-500" size={36} />
                  <p className="text-gray-500">Preparing your preview...</p>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center">
              {isPrintPdf
                ? 'This preview and the downloaded PDF come from the same print-quality master, so their layout will match. Click any page to inspect the details.'
                : 'On-screen preview only. Click to expand, or download and email the full-resolution PNG.'}
            </p>
          </div>
        </div>

        {/* Details */}
        <div className="order-1 lg:order-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {flyer.category && (
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-3">
                {flyer.category}
              </span>
            )}
            <h1 className="text-2xl font-bold text-gray-900 font-display">{flyer.title}</h1>
            {flyer.description && <p className="text-gray-600 mt-2">{flyer.description}</p>}
            {flyer.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100 mt-4">
                {flyer.tags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-sm">#{tag}</span>
                ))}
              </div>
            )}
          </div>

          {/* Size Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Select Size</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sizes.map((size) => (
                <button
                  key={size.id}
                  onClick={() => setSelectedSize(size)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedSize?.id === size.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedSize?.id === size.id ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {getSizeIcon(size.category)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">{size.name}</p>
                      <p className="text-xs text-gray-500">{size.description}</p>
                    </div>
                    {selectedSize?.id === size.id && <Check className="text-primary-500" size={20} />}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Get Your Flyer</h2>
            <div className="space-y-3">
              <button
                onClick={handleDownload}
                disabled={!selectedSize || downloading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {downloading ? <Loader className="animate-spin" size={20} /> : <Download size={20} />}
                {downloading ? 'Downloading...' : isPrintPdf ? 'Download Print-Quality PDF' : 'Download High-Resolution PNG'}
              </button>
              <button
                onClick={handleEmail}
                disabled={!selectedSize || emailing}
                className="btn-outline w-full flex items-center justify-center gap-2"
              >
                {emailing ? <Loader className="animate-spin" size={20} /> : <Mail size={20} />}
                {emailing ? 'Sending...' : isPrintPdf ? 'Email Print-Quality PDF' : 'Email High-Resolution PNG'}
              </button>
            </div>
            {supportSession && <p className="mt-3 text-center text-xs text-gray-600">Email exports will go to this account: {user?.email}.</p>}
            <p className="text-xs text-gray-400 mt-4 text-center">
              {isPrintPdf
                ? 'Your branding will be applied to an 8.5 x 11 inch, 300-DPI PDF that matches the on-screen preview.'
                : 'Your branding will be applied at the selected pixel dimensions.'}
            </p>
          </div>

          {/* Suggested Social Post */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-600 flex items-center justify-center shrink-0">
                <Sparkles size={20} />
              </div>
              <div>
                <h2 className="font-semibold text-gray-800">Suggested Social Post</h2>
                <p className="text-sm text-gray-500 mt-1">Edit anything you like before copying or sharing.</p>
              </div>
            </div>

            <label htmlFor="social-caption" className="block text-sm font-medium text-gray-700 mb-2">
              Caption
            </label>
            <textarea
              id="social-caption"
              value={socialCaption}
              onChange={(event) => setSocialCaption(event.target.value)}
              rows={8}
              className="input-field resize-y text-sm leading-relaxed"
            />

            <label htmlFor="social-hashtags" className="block text-sm font-medium text-gray-700 mt-4 mb-2">
              Hashtags
            </label>
            <textarea
              id="social-hashtags"
              value={socialHashtags}
              onChange={(event) => setSocialHashtags(event.target.value)}
              rows={3}
              className="input-field resize-y text-sm leading-relaxed"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
              <button
                type="button"
                onClick={handleCopySocialPost}
                className="btn-outline w-full flex items-center justify-center gap-2"
              >
                <Copy size={19} /> Copy Caption + Hashtags
              </button>
              <button
                type="button"
                onClick={handleShare}
                disabled={sharing}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                {sharing ? <Loader className="animate-spin" size={19} /> : <Share2 size={19} />}
                {sharing
                  ? 'Preparing Image...'
                  : supportsNativeFileShare
                    ? shareAsset ? 'Share Now' : 'Share Branded Image'
                    : shareAsset ? 'Download Image + Copy Text' : 'Prepare Image + Copy Text'}
              </button>
            </div>

            {socialPlatforms.some(item => item.connected) && (
              <div className="mt-5 border-t border-gray-100 pt-5">
                <p className="mb-3 text-sm font-semibold text-gray-800">Publish directly</p>
                {supportSession && <p className="mb-3 text-sm text-gray-600">Direct publishing is unavailable during support access.</p>}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {socialPlatforms.filter(item => item.connected).map(item => (
                    <button
                      key={item.platform}
                      type="button"
                      onClick={() => handleDirectPublish(item.platform)}
                      disabled={Boolean(publishingPlatform) || Boolean(supportSession)}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary-600 px-3 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
                    >
                      {publishingPlatform === item.platform ? <Loader className="animate-spin" size={17} /> : <Send size={17} />}
                      {SOCIAL_LABELS[item.platform]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!supportsNativeFileShare && (
              <div className="mt-5 border-t border-gray-100 pt-5">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-800">Open a social platform</p>
                  {!socialPlatforms.some(item => item.connected) && (
                    <Link to="/profile#connected-social-accounts" className="text-xs font-semibold text-primary-600 hover:text-primary-700">
                      Connect accounts
                    </Link>
                  )}
                </div>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  Each button opens the platform, downloads the branded image, and copies your caption so it is ready to paste.
                </p>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {Object.keys(DESKTOP_SOCIAL_URLS).map(platform => (
                    <button
                      key={platform}
                      type="button"
                      onClick={() => handleDesktopPlatform(platform)}
                      disabled={sharing}
                      aria-label={`Open ${SOCIAL_LABELS[platform]} and prepare post`}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-gray-200 px-2 text-sm font-semibold text-gray-700 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 disabled:opacity-50"
                    >
                      <SocialPlatformIcon platform={platform} />
                      <span className="hidden sm:inline">{SOCIAL_LABELS[platform]}</span>
                      <ExternalLink size={14} className="text-gray-400" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-4 text-center">
              The hub uses your selected social size, or the Instagram/Facebook post size when a print size is selected.
            </p>
          </div>
        </div>
      </div>

      {isPreviewOpen && previewUrls.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex bg-gray-950/80 p-2 backdrop-blur-sm sm:p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsPreviewOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="expanded-preview-title"
            className="m-auto flex h-[calc(100vh-1rem)] w-full max-w-[1800px] flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:h-[calc(100vh-2rem)]"
          >
            <header className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-4 py-3 sm:px-5">
              <div className="min-w-0">
                <h2 id="expanded-preview-title" className="truncate font-semibold text-gray-900">
                  {flyer.title}
                </h2>
                <p className="text-xs text-gray-500">
                  {previewPageCount > 1 ? `${previewPageCount}-page preview` : 'Expanded preview'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsPreviewActualSize(value => !value)}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:border-primary-300 hover:text-primary-700"
                  aria-label={isPreviewActualSize ? 'Fit all pages in the expanded preview' : 'View at full preview resolution'}
                >
                  {isPreviewActualSize ? <ZoomOut size={17} /> : <ZoomIn size={17} />}
                  <span className="hidden sm:inline">{isPreviewActualSize ? 'Fit pages' : 'Full detail'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
                  aria-label="Close expanded preview"
                >
                  <X size={22} />
                </button>
              </div>
            </header>

            <div className={`flex-1 overflow-auto bg-gray-900 p-4 sm:p-6 ${isPreviewActualSize ? '' : 'flex items-start justify-center'}`}>
              <div className={isPreviewActualSize
                ? 'flex w-max min-w-full items-start justify-start gap-6'
                : `grid w-full items-start justify-center gap-4 ${previewPageCount > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`
              }>
                {previewUrls.map((previewUrl, index) => (
                  <figure
                    key={`expanded-${selectedSize?.slug}-${index + 1}`}
                    className={isPreviewActualSize ? 'w-[1275px] shrink-0' : 'mx-auto w-full max-w-[720px]'}
                  >
                    <figcaption className="mb-2 text-center text-xs font-semibold uppercase tracking-[0.14em] text-gray-300">
                      Page {index + 1} of {previewPageCount}
                    </figcaption>
                    <img
                      src={previewUrl}
                      alt={`${flyer.title} expanded - page ${index + 1} of ${previewPageCount}`}
                      className="h-auto w-full bg-white shadow-2xl"
                    />
                  </figure>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default FlyerDetail;
